import { useMemo, useState } from "react";
import ProductCard from "@/components/product/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { categories } from "@/data/products";

export default function ProductsPage({ products }) {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchText, setSearchText] = useState("");

  const filteredProducts = useMemo(() => {
    return products.filter((item) => {
      const categoryMatch = activeCategory === "All" || item.category === activeCategory;
      const query = searchText.trim().toLowerCase();
      const searchMatch =
        !query ||
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.specs.some((spec) => spec.toLowerCase().includes(query));

      return categoryMatch && searchMatch;
    });
  }, [products, activeCategory, searchText]);

  return (
    <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[260px_1fr] lg:px-8">
      <aside className="h-fit rounded-sm border border-slate-200 bg-white p-5 lg:sticky lg:top-28">
        <h1 data-testid="products-title" className="font-heading text-3xl font-bold text-slate-900">
          Product Catalog
        </h1>
        <p data-testid="products-subtitle" className="mt-2 text-sm text-slate-600">
          Filter categories and send inquiry for any product instantly.
        </p>

        <Input
          data-testid="products-search-input"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search product..."
          className="mt-4 rounded-sm border-slate-300 focus-visible:ring-orange-500"
        />

        <div className="mt-4 space-y-2">
          {categories.map((category) => (
            <Button
              key={category}
              data-testid={`products-category-filter-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setActiveCategory(category)}
              variant={activeCategory === category ? "default" : "outline"}
              className={`w-full justify-start rounded-sm ${
                activeCategory === category
                  ? "bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {category}
            </Button>
          ))}
        </div>
      </aside>

      <section>
        <div className="mb-5 flex items-center justify-between">
          <p data-testid="products-result-count" className="text-sm font-medium text-slate-700">
            Showing {filteredProducts.length} products
          </p>
        </div>

        {filteredProducts.length === 0 ? (
          <div data-testid="products-empty-state" className="rounded-sm border border-slate-200 bg-white p-8 text-center">
            <p className="font-semibold text-slate-900">No products found</p>
            <p className="mt-2 text-sm text-slate-500">Try another category or search keyword.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
